import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiBriefcase,
  FiBook,
  FiTarget,
  FiPlus,
  FiX,
  FiCamera,
  FiTrash2,
  FiSave,
  FiLoader,
  FiLink,
  FiGlobe,
  FiEdit3,
  FiCheck,
} from "react-icons/fi";
import { useUserContext } from "../../../context/UserContext";
import { toast } from "react-toastify";
import "./ProfileSettings.css";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15,
    },
  },
};

const socialPlatforms = [
  { value: "google-scholar", label: "Google Scholar", icon: "🎓" },
  { value: "linkedin", label: "LinkedIn", icon: "💼" },
  { value: "twitter", label: "Twitter/X", icon: "𝕏" },
  { value: "github", label: "GitHub", icon: "💻" },
  { value: "orcid", label: "ORCID", icon: "🆔" },
  { value: "website", label: "Personal Website", icon: "🌐" },
];

const ProfileSettings = () => {
  const {
    user,
    updateProfile,
    updateProfilePicture,
    removeProfilePicture,
    fetchUserProfile,
  } = useUserContext();

  const [profileData, setProfileData] = useState({
    name: "",
    professionalRole: "",
    institutionEmail: "",
    email: "",
    field: "",
    bio: "",
    education: "",
    goals: [],
    goalsText: "",
    researchInterests: [],
    interests: [],
  });

  const [externalLinks, setExternalLinks] = useState([]);
  const [newInterest, setNewInterest] = useState("");
  const [newResearchInterest, setNewResearchInterest] = useState("");
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLink, setNewLink] = useState({
    platform: "",
    url: "",
    isPublic: true,
  });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || "",
        professionalRole: user.professionalRole || "",
        institutionEmail: user.institutionEmail || "",
        email: user.email || "",
        field: user.field || "",
        bio: user.bio || "",
        education: user.education || "",
        // Handle goals - convert array format to goalsText string for display
        goals: user.goals || [],
        goalsText: typeof user.goalsText === 'string' 
          ? user.goalsText 
          : (Array.isArray(user.goals) 
              ? user.goals.map(g => g.type === 'custom' ? g.customText : g.type).filter(Boolean).join(', ')
              : (typeof user.goals === 'string' ? user.goals : '')),
        researchInterests: user.researchInterests || [],
        interests: user.interests || [],
      });
      setExternalLinks(user.externalLinks || []);
      setPreviewUrl(user.profilePhotoUrl);
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  // Interests Management
  const addInterest = () => {
    if (newInterest.trim() && profileData.interests.length < 10) {
      if (!profileData.interests.includes(newInterest.trim())) {
        setProfileData((prev) => ({
          ...prev,
          interests: [...prev.interests, newInterest.trim()],
        }));
        setNewInterest("");
        setHasChanges(true);
      }
    }
  };

  const removeInterest = (index) => {
    setProfileData((prev) => ({
      ...prev,
      interests: prev.interests.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  };

  // Research Interests Management
  const addResearchInterest = () => {
    if (
      newResearchInterest.trim() &&
      profileData.researchInterests.length < 10
    ) {
      if (!profileData.researchInterests.includes(newResearchInterest.trim())) {
        setProfileData((prev) => ({
          ...prev,
          researchInterests: [
            ...prev.researchInterests,
            newResearchInterest.trim(),
          ],
        }));
        setNewResearchInterest("");
        setHasChanges(true);
      }
    }
  };

  const removeResearchInterest = (index) => {
    setProfileData((prev) => ({
      ...prev,
      researchInterests: prev.researchInterests.filter((_, i) => i !== index),
    }));
    setHasChanges(true);
  };

  // External Links Management
  const addExternalLink = () => {
    if (newLink.platform && newLink.url && externalLinks.length < 5) {
      const existingIndex = externalLinks.findIndex(
        (link) => link.platform === newLink.platform
      );

      if (existingIndex >= 0) {
        const updated = [...externalLinks];
        updated[existingIndex] = newLink;
        setExternalLinks(updated);
      } else {
        setExternalLinks([...externalLinks, newLink]);
      }

      setNewLink({ platform: "", url: "", isPublic: true });
      setShowAddLink(false);
      setHasChanges(true);
    }
  };

  const removeExternalLink = (index) => {
    setExternalLinks((prev) => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  // Photo Management
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File size must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
      setHasChanges(true);
    }
  };

  const uploadPhoto = async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      await updateProfilePicture(selectedFile);
      setSelectedFile(null);
      toast.success("Profile picture updated successfully!");
    } catch (error) {
      toast.error("Failed to update profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemovePhoto = async () => {
    try {
      setIsUploading(true);
      await removeProfilePicture();
      setPreviewUrl(null);
      setSelectedFile(null);
      toast.success("Profile picture removed");
    } catch (error) {
      toast.error("Failed to remove profile picture");
    } finally {
      setIsUploading(false);
    }
  };

  // Save Profile
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Properly format goals array - ensure it's valid objects, not strings
      const cleanedGoals = Array.isArray(profileData.goals)
        ? profileData.goals
            .filter(goal => goal && typeof goal === 'object' && goal.type)
            .map(goal => ({
              type: goal.type,
              ...(goal.customText ? { customText: goal.customText } : {})
            }))
        : [];

      const updateData = {
        name: profileData.name,
        bio: profileData.bio,
        education: profileData.education,
        field: profileData.field,
        interests: profileData.interests,
        researchInterests: profileData.researchInterests,
        professionalRole: profileData.professionalRole,
        // Send properly formatted goals array and goalsText
        goals: cleanedGoals,
        goalsText: profileData.goalsText,
        institutionEmail: profileData.institutionEmail,
        externalLinks: externalLinks,
      };

      await updateProfile(updateData);
      await fetchUserProfile();
      setHasChanges(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlatformIcon = (platform) => {
    const p = socialPlatforms.find((sp) => sp.value === platform);
    return p ? p.icon : "🔗";
  };

  const getPlatformLabel = (platform) => {
    const p = socialPlatforms.find((sp) => sp.value === platform);
    return p ? p.label : platform;
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="stg-content-wrapper"
    >
      {/* Section Header */}
      <div className="stg-section-header">
        <h1 className="stg-section-title">
          <span className="stg-section-title-icon stg-indigo">
            <FiUser />
          </span>
          Profile Settings
          {hasChanges && (
            <span
              className="stg-status stg-status-warning"
              style={{ marginLeft: "12px", fontSize: "12px" }}
            >
              Unsaved changes
            </span>
          )}
        </h1>
        <p className="stg-section-description">
          Manage your profile information, research interests, and external
          links
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Profile Photo Card */}
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiCamera /> Profile Photo
            </h3>
          </div>
          <div className="stg-card-body">
            <div className="stg-avatar-section">
              <div className="stg-avatar-wrapper">
                <img
                  src={
                    previewUrl ||
                    `https://ui-avatars.com/api/?name=${encodeURIComponent(
                      profileData.name || "User"
                    )}&background=6366f1&color=fff&size=200`
                  }
                  alt="Profile"
                  className="stg-avatar"
                />
                <label className="stg-avatar-edit">
                  <FiCamera />
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    onChange={handlePhotoChange}
                  />
                </label>
              </div>
              <div className="stg-avatar-info">
                <h4>Profile Picture</h4>
                <p>JPG, PNG, GIF or WebP. Max size 5MB.</p>
                <div className="stg-avatar-actions">
                  {selectedFile && (
                    <button
                      type="button"
                      className="stg-btn stg-btn-primary stg-btn-sm"
                      onClick={uploadPhoto}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <>
                          <FiLoader className="stg-spin" /> Uploading...
                        </>
                      ) : (
                        <>
                          <FiSave /> Save Photo
                        </>
                      )}
                    </button>
                  )}
                  {previewUrl && !selectedFile && (
                    <button
                      type="button"
                      className="stg-btn stg-btn-danger stg-btn-sm"
                      onClick={handleRemovePhoto}
                      disabled={isUploading}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Basic Information Card */}
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiEdit3 /> Basic Information
            </h3>
          </div>
          <div className="stg-card-body">
            <div className="stg-form-row">
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiUser /> Full Name{" "}
                  <span className="stg-form-label-required">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={profileData.name}
                  onChange={handleInputChange}
                  className="stg-input"
                  placeholder="Your full name"
                  required
                  maxLength={100}
                />
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiBriefcase /> Professional Role
                </label>
                <input
                  type="text"
                  name="professionalRole"
                  value={profileData.professionalRole}
                  onChange={handleInputChange}
                  className="stg-input"
                  placeholder="e.g., Research Scientist, PhD Student"
                  maxLength={100}
                />
              </div>
            </div>

            <div className="stg-form-row">
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiMail /> Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={profileData.email}
                  className="stg-input"
                  disabled
                />
                <span className="stg-form-hint">Email cannot be changed</span>
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiMail /> Institution Email
                </label>
                <input
                  type="email"
                  name="institutionEmail"
                  value={profileData.institutionEmail}
                  onChange={handleInputChange}
                  className="stg-input"
                  placeholder="your.name@university.edu"
                />
              </div>
            </div>

            <div className="stg-form-row">
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiBook /> Research Field
                </label>
                <input
                  type="text"
                  name="field"
                  value={profileData.field}
                  onChange={handleInputChange}
                  className="stg-input"
                  placeholder="e.g., Computer Science, Biology"
                  maxLength={100}
                />
              </div>
              <div className="stg-form-group">
                <label className="stg-form-label">
                  <FiBook /> Education
                </label>
                <input
                  type="text"
                  name="education"
                  value={profileData.education}
                  onChange={handleInputChange}
                  className="stg-input"
                  placeholder="e.g., PhD in Computer Science, MIT"
                  maxLength={300}
                />
              </div>
            </div>

            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiEdit3 /> Bio
              </label>
              <textarea
                name="bio"
                value={profileData.bio}
                onChange={handleInputChange}
                className="stg-input stg-textarea"
                placeholder="Write a brief bio about yourself and your research..."
                rows={4}
                maxLength={500}
              />
              <span className="stg-form-hint">
                {profileData.bio.length}/500 characters
              </span>
            </div>

            <div className="stg-form-group">
              <label className="stg-form-label">
                <FiTarget /> Research Goals
              </label>
              <textarea
                name="goalsText"
                value={profileData.goalsText}
                onChange={handleInputChange}
                className="stg-input stg-textarea"
                placeholder="Describe your research goals and what you're looking for..."
                rows={3}
                maxLength={1000}
              />
            </div>
          </div>
        </motion.div>

        {/* Interests Card */}
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiBook /> Interests & Expertise
            </h3>
          </div>
          <div className="stg-card-body">
            {/* General Interests */}
            <div className="stg-form-group">
              <label className="stg-form-label">Interests (max 10)</label>
              <div className="stg-tags">
                <AnimatePresence>
                  {profileData.interests.map((interest, index) => (
                    <motion.span
                      key={interest}
                      className="stg-tag"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      {interest}
                      <button
                        type="button"
                        className="stg-tag-remove"
                        onClick={() => removeInterest(index)}
                      >
                        <FiX size={12} />
                      </button>
                    </motion.span>
                  ))}
                </AnimatePresence>
                {profileData.interests.length < 10 && (
                  <div className="stg-flex stg-gap-sm stg-items-center">
                    <input
                      type="text"
                      value={newInterest}
                      onChange={(e) => setNewInterest(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" && (e.preventDefault(), addInterest())
                      }
                      className="stg-input"
                      placeholder="Add interest..."
                      style={{ width: "180px" }}
                      maxLength={100}
                    />
                    <button
                      type="button"
                      className="stg-btn stg-btn-secondary stg-btn-sm"
                      onClick={addInterest}
                    >
                      <FiPlus />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Research Interests */}
            <div
              className="stg-form-group stg-mt-0"
              style={{ marginTop: "24px" }}
            >
              <label className="stg-form-label">
                Research Interests (max 10)
              </label>
              <div className="stg-tags">
                <AnimatePresence>
                  {profileData.researchInterests.map((interest, index) => (
                    <motion.span
                      key={interest}
                      className="stg-tag"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.8 }}
                    >
                      {interest}
                      <button
                        type="button"
                        className="stg-tag-remove"
                        onClick={() => removeResearchInterest(index)}
                      >
                        <FiX size={12} />
                      </button>
                    </motion.span>
                  ))}
                </AnimatePresence>
                {profileData.researchInterests.length < 10 && (
                  <div className="stg-flex stg-gap-sm stg-items-center">
                    <input
                      type="text"
                      value={newResearchInterest}
                      onChange={(e) => setNewResearchInterest(e.target.value)}
                      onKeyPress={(e) =>
                        e.key === "Enter" &&
                        (e.preventDefault(), addResearchInterest())
                      }
                      className="stg-input"
                      placeholder="Add research interest..."
                      style={{ width: "200px" }}
                      maxLength={100}
                    />
                    <button
                      type="button"
                      className="stg-btn stg-btn-secondary stg-btn-sm"
                      onClick={addResearchInterest}
                    >
                      <FiPlus />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* External Links Card */}
        <motion.div variants={itemVariants} className="stg-card">
          <div className="stg-card-header">
            <h3 className="stg-card-title">
              <FiLink /> External Links
            </h3>
            <div className="stg-card-actions">
              {externalLinks.length < 5 && (
                <button
                  type="button"
                  className="stg-btn stg-btn-secondary stg-btn-sm"
                  onClick={() => setShowAddLink(true)}
                >
                  <FiPlus /> Add Link
                </button>
              )}
            </div>
          </div>
          <div className="stg-card-body">
            {externalLinks.length > 0 ? (
              <div className="stg-link-list">
                <AnimatePresence>
                  {externalLinks.map((link, index) => (
                    <motion.div
                      key={index}
                      className="stg-link-item"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <div
                        className={`stg-link-icon ${link.platform}`}
                        style={{ fontSize: "20px" }}
                      >
                        {getPlatformIcon(link.platform)}
                      </div>
                      <div className="stg-link-info">
                        <p className="stg-link-platform">
                          {getPlatformLabel(link.platform)}
                        </p>
                        <p className="stg-link-url">{link.url}</p>
                      </div>
                      <div className="stg-link-actions">
                        <button
                          type="button"
                          className="stg-btn stg-btn-ghost stg-btn-icon stg-btn-sm"
                          onClick={() => removeExternalLink(index)}
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="stg-empty">
                <div className="stg-empty-icon">
                  <FiGlobe />
                </div>
                <h4 className="stg-empty-title">No external links</h4>
                <p className="stg-empty-text">
                  Add your social profiles and research links
                </p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Add Link Modal - Moved outside card for proper positioning */}
        <AnimatePresence>
          {showAddLink && (
            <motion.div
              className="stg-modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowAddLink(false)}
            >
              <motion.div
                className="stg-modal"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="stg-modal-header">
                  <h3 className="stg-modal-title">Add External Link</h3>
                  <button
                    type="button"
                    className="stg-modal-close"
                    onClick={() => setShowAddLink(false)}
                  >
                    <FiX />
                  </button>
                </div>
                <div className="stg-modal-body">
                  <div className="stg-add-link-form">
                    <div className="stg-form-group">
                      <label className="stg-form-label">Platform</label>
                      <select
                        value={newLink.platform}
                        onChange={(e) =>
                          setNewLink({
                            ...newLink,
                            platform: e.target.value,
                          })
                        }
                        className="stg-select"
                      >
                        <option value="">Select platform...</option>
                        {socialPlatforms.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.icon} {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="stg-form-group">
                      <label className="stg-form-label">URL</label>
                      <input
                        type="url"
                        value={newLink.url}
                        onChange={(e) =>
                          setNewLink({ ...newLink, url: e.target.value })
                        }
                        className="stg-input"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="stg-toggle-wrapper" style={{ borderBottom: "none" }}>
                      <div className="stg-toggle-info">
                        <p className="stg-toggle-label">Public Link</p>
                        <p className="stg-toggle-description">
                          Make this link visible on your profile
                        </p>
                      </div>
                      <label className="stg-toggle">
                        <input
                          type="checkbox"
                          checked={newLink.isPublic}
                          onChange={(e) =>
                            setNewLink({
                              ...newLink,
                              isPublic: e.target.checked,
                            })
                          }
                        />
                        <span className="stg-toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                </div>
                <div className="stg-modal-footer">
                  <button
                    type="button"
                    className="stg-btn stg-btn-secondary"
                    onClick={() => setShowAddLink(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="stg-btn stg-btn-primary"
                    onClick={addExternalLink}
                    disabled={!newLink.platform || !newLink.url}
                  >
                    <FiPlus /> Add Link
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Save Button */}
        <motion.div
          variants={itemVariants}
          className="stg-card-footer"
          style={{
            background: "transparent",
            border: "none",
            padding: "0",
            marginTop: "8px",
          }}
        >
          <button
            type="submit"
            className="stg-btn stg-btn-primary stg-btn-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FiLoader className="stg-spin" /> Saving...
              </>
            ) : (
              <>
                <FiCheck /> Save Changes
              </>
            )}
          </button>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default ProfileSettings;
